import * as React from 'react';
import {
  Check,
  CircleCheck,
  CloudUpload,
  Info,
  Loader2,
  Pause,
  Play,
  Sparkles,
  Star,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { showGlobalModal } from 'core/components/GlobalModal';
import { PaginationBar } from 'core/components/PaginationBar';
import { apiFileUpload } from 'core/utils/fileUpload/api';
import httpclient from 'libs/Axios';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { DigitalMarketingDeleteModal } from 'features/DigitalMarketing/components/DigitalMarketingDeleteModal';
import { editWebsiteContentInputClassName } from 'features/DigitalMarketing/constants/formStyles';
import {
  extractUploadedUrl,
  getApiErrorMessage,
} from 'features/DigitalMarketing/utils/apiResponse';
import avatarOne from 'assets/images/profile.webp';
import avatarTwo from 'assets/images/ChatBotAvatar.webp';
import avatarThree from 'assets/images/SupportStaffFemale.webp';
import avatarFour from 'assets/images/SupportStaffMale.webp';
import avatarRequirementGood from 'assets/images/DigitalMarketing/avatar-requirement-good.webp';
import avatarRequirementBad from 'assets/images/DigitalMarketing/avatar-requirement-bad.webp';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMInput,
  DMLabel,
  DMSelect,
  DMText,
  DMBr,
  DMOption,
  DMVideo,
} from '../components/Primitives';

type AvatarOption = {
  id: number;
  image: string;
  label: string;
  background: string;
  gender: 'Male' | 'Female';
  videoUrl?: string;
  status?: string;
  isDefault?: boolean;
  raw?: any;
};

type AvatarVoiceOption = {
  id: number;
  label: string;
  gender: 'Male' | 'Female';
  voiceUrl: string;
};

const avatarImages = [avatarThree, avatarTwo, avatarOne, avatarFour];

const fallbackAvatarOptions: AvatarOption[] = Array.from({ length: 12 }).map(
  (_, index) => ({
    id: index + 1,
    image: avatarImages[index % avatarImages.length],
    label: `Avatar ${index + 1}`,
    gender: index % 2 === 0 ? 'Female' : 'Male',
    background: [
      '#ead9f6',
      '#eef2f7',
      '#f8e7ef',
      '#e9f2f1',
      '#fbf1df',
      '#efd7f7',
      '#f7eadc',
      '#e8edf3',
      '#edf3ef',
      '#efe8e2',
      '#e7edf8',
      '#e7f2ff',
    ][index % 12],
  })
);

const fallbackVoiceOptions: AvatarVoiceOption[] = Array.from({
  length: 10,
}).map((_, index) => ({
  id: index + 1,
  label: `Voice ${String(index + 1).padStart(2, '0')}`,
  gender: index % 2 === 0 ? 'Female' : 'Male',
  voiceUrl: '',
}));

const goodPhotoExamples = [
  avatarRequirementGood,
  avatarTwo,
  avatarThree,
  avatarFour,
];
const badPhotoExamples = [
  avatarRequirementBad,
  avatarOne,
  avatarRequirementBad,
  avatarOne,
];

const getAvatarBackground = (index: number) =>
  [
    '#ead9f6',
    '#eef2f7',
    '#f8e7ef',
    '#e9f2f1',
    '#fbf1df',
    '#efd7f7',
    '#f7eadc',
    '#e8edf3',
    '#edf3ef',
    '#efe8e2',
    '#e7edf8',
    '#e7f2ff',
  ][index % 12];

const normalizeGender = (gender: any): 'Male' | 'Female' =>
  String(gender || '').toUpperCase() === 'MALE' ? 'Male' : 'Female';

const normalizeBoolean = (value: any) =>
  value === true || value === 1 || String(value || '').toLowerCase() === 'true';

const mapAvatarRecord = (item: any, index: number): AvatarOption => ({
  id: Number(
    item?.avatar_info_id || item?.AVATAR_INFO_ID || item?.id || index + 1
  ),
  image:
    item?.thumbnail ||
    item?.THUMBNAIL ||
    fallbackAvatarOptions[index % fallbackAvatarOptions.length]?.image ||
    avatarOne,
  label: item?.name || item?.NAME || `Avatar ${index + 1}`,
  background: getAvatarBackground(index),
  gender:
    item?.gender || item?.GENDER
      ? normalizeGender(item?.gender || item?.GENDER)
      : index % 2 === 0
      ? 'Female'
      : 'Male',
  videoUrl: item?.video_url || item?.VIDEO_URL || '',
  status: item?.avatar_status || item?.AVATAR_STATUS || '',
  isDefault: normalizeBoolean(item?.is_default ?? item?.IS_DEFAULT),
  raw: item,
});

const mapVoiceRecord = (item: any, index: number): AvatarVoiceOption => ({
  id: Number(
    item?.avatar_voice_id || item?.AVATAR_VOICE_ID || item?.id || index + 1
  ),
  label: `Voice ${String(index + 1).padStart(2, '0')}`,
  gender: normalizeGender(item?.gender || item?.GENDER),
  voiceUrl: item?.voice_url || item?.VOICE_URL || '',
});

const isProcessingAvatar = (avatar?: AvatarOption) =>
  String(avatar?.status || '').toUpperCase() === 'PROCESSING';

const isFailedAvatar = (avatar?: AvatarOption) =>
  String(avatar?.status || '').toUpperCase() === 'FAILED';

function ProcessingOverlay() {
  return (
    <DMDiv className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/65 backdrop-blur-[1px]">
      <Loader2
        className="h-[3vh] w-[3vh] animate-spin text-blue-600"
        strokeWidth={2.4}
      />
      <DMText
        as="span"
        className="mt-[1vh] text-[1.35vh] font-bold uppercase tracking-wide text-gray-700"
      >
        Processing
      </DMText>
    </DMDiv>
  );
}

function FailedAvatarPlaceholder({ className = '' }: { className?: string }) {
  return (
    <DMDiv
      className={`flex h-full w-full items-center justify-center bg-gray-200 text-[2vh] font-bold uppercase tracking-wide text-gray-600 ${className}`}
    >
      FAILED
    </DMDiv>
  );
}

function AvatarCreatedModal({
  onGoToDashboard,
}: {
  onGoToDashboard: () => void;
}) {
  const progressBarRef = React.useRef<HTMLDivElement>(null);
  const progressTextRef = React.useRef<HTMLSpanElement>(null);

  React.useEffect(() => {
    let nextProgress = 0;
    const intervalId = window.setInterval(() => {
      nextProgress = Math.min(62, nextProgress + 1);

      if (progressBarRef.current) {
        progressBarRef.current.style.width = `${nextProgress}%`;
      }

      if (progressTextRef.current) {
        progressTextRef.current.textContent = `${nextProgress}%`;
      }

      if (nextProgress >= 62) {
        window.clearInterval(intervalId);
      }
    }, 380);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <DMDiv className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 px-[2vw]">
      <DMDiv className="w-[31vw] max-w-[430px] rounded-[28px] border border-gray-100 bg-white px-[2.7vw] py-[5vh] text-center shadow-[0_22px_65px_rgba(15,23,42,0.28)]">
        <DMDiv className="relative mx-auto flex h-[14vh] w-[14vh] items-center justify-center">
          <DMText
            as="span"
            className="absolute h-[11.2vh] w-[11.2vh] rounded-full bg-blue-100"
          />
          <DMText
            as="span"
            className="absolute h-[8vh] w-[8vh] rounded-full bg-blue-600 shadow-[0_16px_30px_rgba(37,99,235,0.35)]"
          />
          <Sparkles
            className="relative h-[4.6vh] w-[4.6vh] fill-white text-white"
            strokeWidth={2.2}
          />
          <DMText
            as="span"
            className="absolute left-[0.9vw] top-[1.6vh] h-[1.4vh] w-[1.4vh] rounded-full bg-purple-300"
          />
          <DMText
            as="span"
            className="absolute right-[1.3vw] top-[2.6vh] h-[1vh] w-[1vh] rounded-full bg-blue-300"
          />
          <DMText
            as="span"
            className="absolute bottom-[3.5vh] right-[1.9vw] h-[1.2vh] w-[1.2vh] rounded-full bg-emerald-300"
          />
          <DMText
            as="span"
            className="absolute bottom-[2vh] left-[1.4vw] h-[1vh] w-[1vh] rounded-full bg-purple-200"
          />
        </DMDiv>

        <DMText
          as="h2"
          className="mt-[2.4vh] text-[3.6vh] font-bold leading-tight text-gray-950"
        >
          Creating Your Avatar...
        </DMText>
        <DMText className="mx-auto mt-[2.3vh] max-w-[25vw] text-[2vh] font-medium leading-[1.45] text-gray-500">
          Please wait while we generate your virtual counsellor.
          <DMBr />
          This may take a few minutes.
        </DMText>

        <DMDiv className="mt-[4.2vh] flex items-center gap-[1.4vw]">
          <DMDiv className="h-[1.5vh] flex-1 overflow-hidden rounded-full bg-gray-100">
            <DMDiv
              ref={progressBarRef}
              className="h-full rounded-full bg-blue-600 transition-[width] duration-300 ease-linear"
              style={{ width: '0%' }}
            />
          </DMDiv>
          <DMText
            as="span"
            ref={progressTextRef}
            className="min-w-[3vw] text-right text-[2vh] font-bold text-gray-600"
          >
            0%
          </DMText>
        </DMDiv>

        <DMDiv className="mx-auto mt-[3.2vh] inline-flex items-center gap-[0.8vw] rounded-[16px] bg-orange-50 px-[2vw] py-[1.8vh] text-[2vh] font-semibold text-orange-500">
          <Info className="h-[2vh] w-[2vh]" strokeWidth={2} />
          <DMText as="span">Estimated time: 5-10 minutes</DMText>
        </DMDiv>

        <DMDiv className="mt-[4.2vh] border-t border-gray-200 pt-[2.5vh]">
          <DMText className="text-[2vh] font-bold text-gray-950">
            You can continue using the platform.
          </DMText>
          <DMText className="mt-[1vh] text-[1.9vh] font-medium text-gray-500">
            We'll notify you once your avatar is ready.
          </DMText>
        </DMDiv>

        <DMButton
          type="button"
          onClick={onGoToDashboard}
          className="mt-[2.4vh] h-[7vh] w-full rounded-[18px] border border-blue-200 bg-blue-50 text-[2vh] font-bold text-blue-600 transition-colors hover:bg-blue-100"
        >
          Go to Dashboard
        </DMButton>
      </DMDiv>
    </DMDiv>
  );
}

export function AvatarSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const authData = useAuth();
  const isCreateMode = location.pathname.endsWith('/create');
  const [selectedAvatarId, setSelectedAvatarId] = React.useState(1);
  const [avatarName, setAvatarName] = React.useState('');
  const [uploadedPhotoPreview, setUploadedPhotoPreview] = React.useState('');
  const [uploadedPhotoFile, setUploadedPhotoFile] = React.useState<File | null>(
    null
  );
  const [selectedVoiceId, setSelectedVoiceId] = React.useState(1);
  const [playingVoiceId, setPlayingVoiceId] = React.useState(0);
  const [showCreatedModal, setShowCreatedModal] = React.useState(false);
  const [avatarFilter, setAvatarFilter] = React.useState<
    'all' | 'default' | 'custom'
  >('all');
  const [voiceFilter, setVoiceFilter] = React.useState<
    'All' | 'Male' | 'Female'
  >('All');
  const [pageIndex, setPageIndex] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(16);
  const [avatarOptions, setAvatarOptions] = React.useState<AvatarOption[]>(
    fallbackAvatarOptions
  );
  const [voiceOptions, setVoiceOptions] =
    React.useState<AvatarVoiceOption[]>(fallbackVoiceOptions);
  const [totalAvatars, setTotalAvatars] = React.useState(
    fallbackAvatarOptions.length
  );
  const [isLoadingAvatars, setIsLoadingAvatars] = React.useState(false);
  const [isLoadingVoices, setIsLoadingVoices] = React.useState(false);
  const [isSubmittingAvatar, setIsSubmittingAvatar] = React.useState(false);
  const [isDeletingAvatar, setIsDeletingAvatar] = React.useState(false);
  const [avatarToDelete, setAvatarToDelete] =
    React.useState<AvatarOption | null>(null);
  const [avatarImageStates, setAvatarImageStates] = React.useState<
    Record<number, 'loading' | 'loaded'>
  >({});
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const previewVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const previewPlaybackTokenRef = React.useRef(0);
  const previewShouldPlayRef = React.useRef(false);
  const shouldAutoplaySelectedAvatarRef = React.useRef(false);
  const avatarRequestIdRef = React.useRef(0);
  const [isPreviewPlaying, setIsPreviewPlaying] = React.useState(false);
  const [isPreviewBuffering, setIsPreviewBuffering] = React.useState(false);
  const createPhotoInputId = React.useId();
  const selectedAvatar =
    avatarOptions.find(avatar => avatar.id === selectedAvatarId) ||
    avatarOptions[0] ||
    fallbackAvatarOptions[0];
  const isSelectedAvatarProcessing = isProcessingAvatar(selectedAvatar);
  const isSelectedAvatarFailed = isFailedAvatar(selectedAvatar);
  const isSelectedAvatarPreviewable = Boolean(
    selectedAvatar.videoUrl &&
      !isSelectedAvatarProcessing &&
      !isSelectedAvatarFailed
  );
  const filteredAvatars = React.useMemo(() => avatarOptions, [avatarOptions]);
  const paginatedAvatars = filteredAvatars;
  const filteredVoiceOptions = React.useMemo(
    () =>
      voiceOptions.filter(
        voice => voiceFilter === 'All' || voice.gender === voiceFilter
      ),
    [voiceFilter, voiceOptions]
  );

  const startAvatarPreview = React.useCallback(
    async (videoElement: HTMLVideoElement) => {
      const playbackToken = ++previewPlaybackTokenRef.current;
      setIsPreviewBuffering(
        videoElement.readyState < HTMLMediaElement.HAVE_FUTURE_DATA
      );

      try {
        await videoElement.play();

        if (
          playbackToken === previewPlaybackTokenRef.current &&
          previewShouldPlayRef.current &&
          previewVideoRef.current === videoElement &&
          videoElement.isConnected
        ) {
          setIsPreviewPlaying(true);
          setIsPreviewBuffering(false);
        }
      } catch (error: any) {
        if (
          playbackToken !== previewPlaybackTokenRef.current ||
          error?.name === 'AbortError'
        ) {
          return;
        }

        previewShouldPlayRef.current = false;
        setIsPreviewPlaying(false);
        setIsPreviewBuffering(false);
      }
    },
    []
  );

  React.useEffect(() => {
    setPageIndex(1);
  }, [avatarFilter]);

  React.useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(totalAvatars / pageSize));

    if (pageIndex > totalPages) {
      setPageIndex(totalPages);
    }
  }, [pageIndex, pageSize, totalAvatars]);

  const fetchAvatars = React.useCallback(async () => {
    const requestId = avatarRequestIdRef.current + 1;
    avatarRequestIdRef.current = requestId;
    setIsLoadingAvatars(true);

    try {
      const filterBy = avatarFilter;
      const response = await httpclient().get(
        `/master/branch/avatar/list?page_index=${pageIndex}&page_size=${pageSize}&filter_by=${filterBy}`
      );
      const data = response?.data?.data || {};
      const avatarItems = Array.isArray(data?.avatars?.items)
        ? data.avatars.items
        : [];
      const mappedAvatars: AvatarOption[] = avatarItems.map(mapAvatarRecord);
      const selectedPreview = data?.selected_avatar_preview;
      const selectedId = Number(
        selectedPreview?.avatar_info_id || selectedPreview?.AVATAR_INFO_ID || 0
      );
      const selectedPreviewAvatar = selectedPreview
        ? mapAvatarRecord(selectedPreview, 0)
        : null;
      const nextAvatars = mappedAvatars;

      const initialImageStates: Record<number, 'loading'> = {};

      nextAvatars.forEach((avatar: AvatarOption) => {
        initialImageStates[avatar.id] = 'loading';
      });

      if (avatarRequestIdRef.current === requestId) {
        shouldAutoplaySelectedAvatarRef.current = false;
        setAvatarOptions(nextAvatars);
        setAvatarImageStates(initialImageStates);
        setTotalAvatars(
          Number(data?.avatars?.total || mappedAvatars.length || 0)
        );

        if (
          selectedId &&
          nextAvatars.some(avatar => avatar.id === selectedId) &&
          !isFailedAvatar(selectedPreviewAvatar || undefined)
        ) {
          setSelectedAvatarId(selectedId);
        } else {
          const firstPreviewableAvatar = nextAvatars.find(
            avatar => !isFailedAvatar(avatar)
          );

          if (firstPreviewableAvatar?.id) {
            setSelectedAvatarId(firstPreviewableAvatar.id);
          }
        }
      }
    } catch (error: any) {
      if (avatarRequestIdRef.current === requestId) {
        showGlobalModal(
          getApiErrorMessage(error, 'Unable to fetch avatars'),
          'error'
        );
        shouldAutoplaySelectedAvatarRef.current = false;
        setAvatarOptions(fallbackAvatarOptions);
        setAvatarImageStates({});
        setTotalAvatars(fallbackAvatarOptions.length);
      }
    } finally {
      if (avatarRequestIdRef.current === requestId) {
        setIsLoadingAvatars(false);
      }
    }
  }, [avatarFilter, pageIndex, pageSize]);

  const fetchVoices = React.useCallback(async () => {
    setIsLoadingVoices(true);

    try {
      const response = await httpclient().get(
        '/master/branch/avatar/voice/list'
      );
      const items = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      const mappedVoices = items.map(mapVoiceRecord);

      setVoiceOptions(
        mappedVoices.length ? mappedVoices : fallbackVoiceOptions
      );
      setSelectedVoiceId(currentVoiceId =>
        mappedVoices.some(voice => voice.id === currentVoiceId)
          ? currentVoiceId
          : mappedVoices[0]?.id || fallbackVoiceOptions[0].id
      );
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to fetch avatar voices'),
        'error'
      );
      setVoiceOptions(fallbackVoiceOptions);
    } finally {
      setIsLoadingVoices(false);
    }
  }, []);

  React.useEffect(() => {
    if (!isCreateMode) {
      fetchAvatars();
    }
  }, [fetchAvatars, isCreateMode]);

  React.useEffect(() => {
    if (isCreateMode) {
      fetchVoices();
    }
  }, [fetchVoices, isCreateMode]);

  React.useEffect(() => {
    previewPlaybackTokenRef.current += 1;
    const shouldAutoplay =
      shouldAutoplaySelectedAvatarRef.current && isSelectedAvatarPreviewable;
    shouldAutoplaySelectedAvatarRef.current = false;
    previewShouldPlayRef.current = shouldAutoplay;
    const videoElement = previewVideoRef.current;

    videoElement?.pause();
    setIsPreviewPlaying(false);
    setIsPreviewBuffering(shouldAutoplay);

    if (shouldAutoplay && videoElement) {
      void startAvatarPreview(videoElement);
    }
  }, [
    isSelectedAvatarPreviewable,
    selectedAvatar.id,
    selectedAvatar.videoUrl,
    startAvatarPreview,
  ]);

  React.useEffect(
    () => () => {
      if (uploadedPhotoPreview) {
        URL.revokeObjectURL(uploadedPhotoPreview);
      }
      audioRef.current?.pause();
    },
    [uploadedPhotoPreview]
  );

  const handleSaveChanges = async () => {
    if (!selectedAvatar?.id) {
      showGlobalModal('Please select an avatar', 'warning');
      return;
    }

    try {
      const response = await httpclient().post('/master/branch/avatar/select', {
        avatar_info_id: selectedAvatar.id,
      });
      showGlobalModal(
        response?.data?.message || 'Avatar selected successfully'
      );
      fetchAvatars();
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to select avatar'),
        'error'
      );
    }
  };

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (
      !['image/png', 'image/jpg', 'image/jpeg', 'image/webp'].includes(
        file.type
      )
    ) {
      showGlobalModal(
        'Only png, jpg, jpeg, or webp files are allowed',
        'error'
      );
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showGlobalModal('Image must be 5MB or less', 'error');
      event.target.value = '';
      return;
    }

    if (uploadedPhotoPreview) {
      URL.revokeObjectURL(uploadedPhotoPreview);
    }

    setUploadedPhotoPreview(URL.createObjectURL(file));
    setUploadedPhotoFile(file);
    event.target.value = '';
  };

  const handleCreateAvatar = async () => {
    if (!avatarName.trim()) {
      showGlobalModal('Please enter avatar name', 'warning');
      return;
    }

    if (!uploadedPhotoFile) {
      showGlobalModal('Please upload avatar photo', 'warning');
      return;
    }

    if (!selectedVoiceId) {
      showGlobalModal('Please select avatar voice', 'warning');
      return;
    }

    setIsSubmittingAvatar(true);

    try {
      const formData = new FormData();
      formData.append('dest', 'DM-CONTENT');
      formData.append('branch_id', authData?.BRANCH_ID as any);
      formData.append('database', authData?.DATABASE as any);
      formData.append('files', uploadedPhotoFile);

      const uploadResponse = await apiFileUpload(
        formData,
        'upload',
        '',
        '',
        true,
        false,
        true
      );
      const imageUrl = extractUploadedUrl(uploadResponse);

      if (!imageUrl) {
        showGlobalModal('Unable to read uploaded image URL', 'error');
        return;
      }

      await httpclient().post('/master/branch/avatar/upsert', {
        AVATAR_NAME: avatarName.trim(),
        IMAGE_URL: imageUrl,
        AVATAR_VOICE_ID: selectedVoiceId,
      });

      setShowCreatedModal(true);
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to create avatar'),
        'error'
      );
    } finally {
      setIsSubmittingAvatar(false);
    }
  };

  const handleConfirmDeleteAvatar = async () => {
    if (!avatarToDelete?.id || avatarToDelete.isDefault) {
      return;
    }

    setIsDeletingAvatar(true);

    try {
      const response = await httpclient().delete(
        `/master/branch/avatar/delete?avatar_info_id=${avatarToDelete.id}`
      );
      showGlobalModal(response?.data?.message || 'Avatar deleted successfully');
      setAvatarToDelete(null);
      fetchAvatars();
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to delete avatar'),
        'error'
      );
    } finally {
      setIsDeletingAvatar(false);
    }
  };

  const handleToggleVoicePreview = async (voice: AvatarVoiceOption) => {
    if (!voice.voiceUrl) {
      setPlayingVoiceId(playingVoiceId === voice.id ? 0 : voice.id);
      return;
    }

    if (playingVoiceId === voice.id) {
      audioRef.current?.pause();
      setPlayingVoiceId(0);
      return;
    }

    try {
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }

      audioRef.current.pause();
      audioRef.current.src = voice.voiceUrl;
      audioRef.current.onended = () => setPlayingVoiceId(0);
      await audioRef.current.play();
      setPlayingVoiceId(voice.id);
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to play avatar voice'),
        'error'
      );
      setPlayingVoiceId(0);
    }
  };

  const handleToggleAvatarPreview = () => {
    if (!isSelectedAvatarPreviewable) {
      return;
    }

    const videoElement = previewVideoRef.current;

    if (!videoElement) {
      return;
    }

    if (previewShouldPlayRef.current) {
      previewShouldPlayRef.current = false;
      previewPlaybackTokenRef.current += 1;
      videoElement.pause();
      setIsPreviewPlaying(false);
      setIsPreviewBuffering(false);
      return;
    }

    previewShouldPlayRef.current = true;
    void startAvatarPreview(videoElement);
  };

  if (isCreateMode) {
    return (
      <DMDiv
        as="main"
        className="relative flex h-full min-h-0 flex-col bg-white px-[1.6vw] py-[2.6vh] font-inter text-gray-950"
      >
        <DMDiv className="mb-[2.9vh] shrink-0">
          <DigitalMarketingBreadcrumb
            className="mb-[1.2vh]"
            items={[
              {
                label: 'Virtual Admission Counsellor',
                path: '/digital-marketing/avatar-selection',
              },
              { label: 'Create Your Avatar' },
            ]}
          />
          <DMDiv className="flex items-start justify-between gap-[2vw]">
            <DMDiv>
              <DMText
                as="h1"
                className="text-[3.45vh] font-bold leading-tight text-gray-950"
              >
                Create Your Avatar
              </DMText>
              <DMText className="mt-[0.8vh] text-[1.7vh] font-semibold leading-tight text-gray-600">
                Your Look, Your Voice, Your Virtual Presence
              </DMText>
            </DMDiv>
            <DMButton
              type="button"
              onClick={handleCreateAvatar}
              disabled={isSubmittingAvatar}
              className="flex h-[6.5vh] min-w-[17.5vw] items-center justify-center gap-[0.8vw] rounded-full bg-blue-600 px-[2.2vw] text-[1.75vh] font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmittingAvatar ? (
                <Loader2
                  className="h-[2.1vh] w-[2.1vh] animate-spin"
                  strokeWidth={2.1}
                />
              ) : (
                <Sparkles
                  className="h-[2.1vh] w-[2.1vh] fill-white"
                  strokeWidth={2.1}
                />
              )}
              <DMText as="span">
                {isSubmittingAvatar ? 'Generating...' : 'Generate Avatar'}
              </DMText>
            </DMButton>
          </DMDiv>
        </DMDiv>

        <DMDiv className="grid min-h-0 flex-1 grid-cols-[0.72fr_1fr] gap-[2.35vw]">
          <DMDiv
            as="section"
            className="flex min-h-0 flex-col rounded-[12px] border border-gray-200 bg-white px-[1.55vw] py-[2.3vh]"
          >
            <DMText as="h2" className="text-[2vh] font-bold text-gray-950">
              1. Upload Photo
            </DMText>
            <DMText className="mt-[0.9vh] text-[1.7vh] font-medium text-gray-600">
              Upload a clear front-facing photo for the best result.
            </DMText>

            {uploadedPhotoPreview ? (
              <>
                <DMDiv className="mt-[1.8vh] h-[45vh] overflow-hidden rounded-[14px] bg-gray-100">
                  <DMImage
                    src={uploadedPhotoPreview}
                    alt="Uploaded avatar"
                    className="h-full w-full object-cover object-center"
                  />
                </DMDiv>
                <DMLabel
                  htmlFor={createPhotoInputId}
                  className="mt-[2vh] flex h-[5.5vh] cursor-pointer items-center justify-center rounded-full border border-blue-200 bg-blue-50 text-[1.55vh] font-semibold text-blue-600 transition-colors hover:bg-blue-100"
                >
                  Re-Upload image
                  <DMInput
                    id={createPhotoInputId}
                    type="file"
                    accept="image/png,image/jpg,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                </DMLabel>
              </>
            ) : (
              <>
                <DMLabel
                  htmlFor={createPhotoInputId}
                  className="mt-[1.8vh] flex h-[21.4vh] cursor-pointer flex-col items-center justify-center rounded-[12px] border border-dashed border-blue-200 bg-gray-50 text-center transition-colors hover:bg-blue-50"
                >
                  <CloudUpload
                    className="h-[4.1vh] w-[4.1vh] text-gray-400"
                    strokeWidth={1.8}
                  />
                  <DMText
                    as="span"
                    className="mt-[1.3vh] text-[1.75vh] font-semibold text-gray-950"
                  >
                    Drag & drop your photo here
                  </DMText>
                  <DMText
                    as="span"
                    className="mt-[1vh] text-[1.45vh] font-medium text-gray-500"
                  >
                    or
                  </DMText>
                  <DMText
                    as="span"
                    className="mt-[0.9vh] rounded-[12px] bg-blue-600 px-[1.45vw] py-[1.2vh] text-[1.45vh] font-bold text-white"
                  >
                    Upload Photo
                  </DMText>
                  <DMText
                    as="span"
                    className="mt-[1.45vh] text-[1.3vh] font-medium text-gray-500"
                  >
                    Supports JPG, PNG, WEBP ( Max 5MB )
                  </DMText>
                  <DMInput
                    id={createPhotoInputId}
                    type="file"
                    accept="image/png,image/jpg,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                </DMLabel>

                <DMDiv className="mt-[1.7vh] flex items-center gap-[0.5vw] text-[1.45vh] font-bold text-emerald-500">
                  <CircleCheck
                    className="h-[1.8vh] w-[1.8vh]"
                    strokeWidth={2.2}
                  />
                  <DMText as="span">Good Photos</DMText>
                </DMDiv>
                <DMDiv className="mt-[0.8vh]">
                  <DMDiv className="h-[15.3vh] overflow-hidden rounded-[6px] bg-gray-100">
                    <DMImage
                      src={goodPhotoExamples[0]}
                      alt="Good avatar example"
                      className="h-full w-full object-contain object-center"
                    />
                  </DMDiv>
                </DMDiv>

                <DMDiv className="mt-[1.4vh] flex items-center gap-[0.5vw] text-[1.45vh] font-bold text-red-500">
                  <X
                    className="h-[1.8vh] w-[1.8vh] rounded-full bg-red-500 p-[0.25vh] text-white"
                    strokeWidth={3}
                  />
                  <DMText as="span">Bad Photos</DMText>
                </DMDiv>
                <DMDiv className="mt-[0.8vh]">
                  <DMDiv className="h-[15.3vh] overflow-hidden rounded-[6px] bg-gray-100">
                    <DMImage
                      src={badPhotoExamples[0]}
                      alt="Bad avatar example"
                      className="h-full w-full object-contain object-center opacity-80"
                    />
                  </DMDiv>
                </DMDiv>

                <DMDiv className="mt-[0.25vh] flex items-start gap-[0.8vw] rounded-[6px] border border-orange-200 bg-orange-50 px-[1vw] py-[1.2vh] text-[1.35vh] font-medium leading-snug text-orange-500">
                  <Info
                    className="mt-[0.1vh] h-[1.7vh] w-[1.7vh] shrink-0"
                    strokeWidth={2}
                  />
                  <DMText as="span">
                    Use a well-lit, front-facing photo. Avoid group photos, side
                    angles and heavy filters.
                  </DMText>
                </DMDiv>
              </>
            )}
          </DMDiv>

          <DMDiv
            as="section"
            className="flex min-h-0 flex-col rounded-[12px] border border-gray-200 bg-white px-[1.55vw] py-[2.3vh]"
          >
            <DMText as="h2" className="text-[2vh] font-bold text-gray-950">
              2. Avatar Details
            </DMText>
            <DMLabel className="mt-[2.1vh] block text-[1.35vh] font-bold text-gray-700">
              Avatar Name
            </DMLabel>
            <DMInput
              value={avatarName}
              onChange={event => setAvatarName(event.target.value.slice(0, 50))}
              placeholder="Enter avatar name"
              className={`mt-[1vh] ${editWebsiteContentInputClassName}`}
            />
            <DMDiv className="mt-[0.6vh] text-right text-[1.2vh] font-medium text-gray-500">
              {avatarName.length} / 50
            </DMDiv>

            <DMDiv className="mt-[0.5vh] flex items-start justify-between gap-[1vw]">
              <DMDiv>
                <DMText as="h2" className="text-[2vh] font-bold text-gray-950">
                  3. Select Voice
                </DMText>
                <DMText className="mt-[0.7vh] text-[1.5vh] font-medium text-gray-600">
                  Choose a voice for your avatar. You can preview each voice
                  before selecting.
                </DMText>
              </DMDiv>
              <DMSelect
                value={voiceFilter}
                onChange={event =>
                  setVoiceFilter(
                    event.target.value as 'All' | 'Male' | 'Female'
                  )
                }
                className="h-[3.8vh] rounded-[8px] border border-gray-300 bg-white px-[0.8vw] text-[1.45vh] font-medium text-gray-800 outline-none"
              >
                <DMOption>All</DMOption>
                <DMOption>Male</DMOption>
                <DMOption>Female</DMOption>
              </DMSelect>
            </DMDiv>

            <DMDiv className="mt-[2vh] grid max-h-[39vh] grid-cols-2 gap-x-[1.2vw] gap-y-[1.55vh] overflow-y-auto pr-[0.45vw]">
              {isLoadingVoices && (
                <DMDiv className="col-span-2 flex h-[16vh] items-center justify-center text-[1.6vh] font-semibold text-gray-500">
                  <Loader2
                    className="mr-[0.6vw] h-[2.2vh] w-[2.2vh] animate-spin"
                    strokeWidth={2.2}
                  />
                  Loading voices...
                </DMDiv>
              )}
              {!isLoadingVoices &&
                filteredVoiceOptions.map((voice, index) => {
                  const isSelected = selectedVoiceId === voice.id;
                  const isPlaying = playingVoiceId === voice.id;

                  return (
                    <DMButton
                      key={voice.id}
                      type="button"
                      onClick={() => setSelectedVoiceId(voice.id)}
                      className={`flex h-[6.2vh] items-center justify-between rounded-[7px] border px-[1.25vw] text-left transition-colors ${
                        isSelected
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-blue-200'
                      }`}
                    >
                      <DMText as="span" className="flex items-center gap-[1vw]">
                        <DMText
                          as="span"
                          role="button"
                          tabIndex={0}
                          onClick={event => {
                            event.stopPropagation();
                            handleToggleVoicePreview(voice);
                          }}
                          onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              event.stopPropagation();
                              handleToggleVoicePreview(voice);
                            }
                          }}
                          className={`flex h-[4vh] w-[4vh] items-center justify-center rounded-full border ${
                            isPlaying
                              ? 'border-blue-600 bg-blue-600 text-white'
                              : 'border-gray-950 bg-white text-gray-950'
                          }`}
                          aria-label={`${isPlaying ? 'Pause' : 'Play'} ${
                            voice.label
                          }`}
                        >
                          {isPlaying ? (
                            <Pause
                              className="h-[1.8vh] w-[1.8vh] fill-current"
                              strokeWidth={2}
                            />
                          ) : (
                            <Play
                              className="ml-[0.2vh] h-[1.8vh] w-[1.8vh] fill-current"
                              strokeWidth={2}
                            />
                          )}
                        </DMText>
                        <DMText
                          as="span"
                          className="text-[1.65vh] font-bold text-gray-950"
                        >
                          {voice.label}
                          <DMText
                            as="span"
                            className="ml-[0.4vw] text-[1.2vh] font-semibold text-gray-400"
                          >
                            {voice.gender}
                          </DMText>
                        </DMText>
                      </DMText>
                      <DMText
                        as="span"
                        className={`flex h-[2.35vh] w-[2.35vh] items-center justify-center rounded-full border-[2px] ${
                          isSelected ? 'border-blue-600' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <DMText
                            as="span"
                            className="h-[1.15vh] w-[1.15vh] rounded-full bg-blue-600"
                          />
                        )}
                      </DMText>
                    </DMButton>
                  );
                })}
            </DMDiv>
          </DMDiv>
        </DMDiv>

        {showCreatedModal && (
          <AvatarCreatedModal
            onGoToDashboard={() => {
              setShowCreatedModal(false);
              navigate('/digital-marketing/dashboard');
            }}
          />
        )}
      </DMDiv>
    );
  }

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col bg-white px-[1.6vw] py-[2.6vh] font-inter text-gray-950"
    >
      <DMDiv className="mb-[2.8vh] flex shrink-0 items-start justify-between gap-[2vw]">
        <DMDiv>
          <DigitalMarketingBreadcrumb
            className="mb-[1.2vh]"
            items={[{ label: 'Virtual Admission Counsellor' }]}
          />
          <DMText
            as="h1"
            className="text-[3.45vh] font-bold leading-tight text-gray-950"
          >
            Virtual admission Counsellor
          </DMText>
          <DMText className="mt-[0.8vh] text-[1.7vh] font-medium leading-tight text-gray-600">
            Select the Avatar that suits your School the Best
          </DMText>
        </DMDiv>

        <DMButton
          type="button"
          onClick={() => navigate('/digital-marketing/avatar-selection/create')}
          className="mt-[1vh] h-[5.6vh] rounded-full border-2 border-blue-600 bg-white px-[2vw] text-[1.7vh] font-semibold text-blue-600 transition-colors hover:bg-blue-50"
        >
          + Create New avatar
        </DMButton>
      </DMDiv>

      <DMDiv className="grid min-h-0 flex-1 grid-cols-[2.1fr_1fr] gap-[1.45vw]">
        <DMDiv
          as="section"
          className="flex min-h-0 flex-col rounded-[10px] border border-gray-200 bg-white px-[1.25vw] py-[2vh]"
        >
          <DMDiv className="mb-[1.8vh] flex items-center justify-between">
            <DMText as="h2" className="text-[1.9vh] font-bold text-gray-900">
              Available Avatars
            </DMText>
            <DMDiv className="flex items-center gap-[0.8vw]">
              <DMText
                as="span"
                className="text-[1.4vh] font-medium text-gray-700"
              >
                Filter by
              </DMText>
              <DMSelect
                value={avatarFilter}
                onChange={event =>
                  setAvatarFilter(
                    event.target.value as 'all' | 'default' | 'custom'
                  )
                }
                className="h-[3.9vh] rounded-[8px] border border-gray-300 bg-white px-[0.9vw] text-[1.45vh] font-medium text-gray-800 outline-none"
              >
                <DMOption value="all">ALL</DMOption>
                <DMOption value="default">DEFAULT</DMOption>
                <DMOption value="custom">CUSTOM</DMOption>
              </DMSelect>
            </DMDiv>
          </DMDiv>

          <DMDiv className="grid min-h-0 flex-1 grid-cols-4 content-start items-start gap-[1.15vw] overflow-y-auto pr-[0.2vw]">
            {isLoadingAvatars &&
              Array.from({ length: pageSize }).map((_, index) => (
                <DMDiv
                  key={`avatar-skeleton-${index}`}
                  className="min-h-[19.3vh] animate-pulse rounded-[10px] border border-gray-200 bg-white p-[1vh]"
                  aria-hidden="true"
                >
                  <DMDiv className="h-[15.4vh] rounded-[7px] bg-gray-200" />
                  <DMDiv className="mx-auto mt-[1.2vh] h-[1.2vh] w-2/3 rounded-full bg-gray-200" />
                </DMDiv>
              ))}
            {!isLoadingAvatars && !paginatedAvatars.length && (
              <DMDiv className="col-span-4 flex h-full min-h-[32vh] items-center justify-center text-[1.7vh] font-semibold text-gray-500">
                No avatars found
              </DMDiv>
            )}
            {!isLoadingAvatars &&
              paginatedAvatars.map(avatar => {
                const isSelected = selectedAvatarId === avatar.id;
                const isFailed = isFailedAvatar(avatar);
                const isImageLoading =
                  !isFailed && avatarImageStates[avatar.id] !== 'loaded';
                const isSelectionDisabled =
                  isFailed || isProcessingAvatar(avatar) || isImageLoading;

                return (
                  <DMDiv
                    key={avatar.id}
                    className={`relative flex h-fit min-h-[19.3vh] flex-col rounded-[10px] border bg-white p-[1vh] transition-colors ${
                      isSelected
                        ? 'border-2 border-blue-600'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {isProcessingAvatar(avatar) && <ProcessingOverlay />}
                    <DMButton
                      type="button"
                      onClick={() => {
                        if (!isSelectionDisabled) {
                          shouldAutoplaySelectedAvatarRef.current = true;
                          setSelectedAvatarId(avatar.id);
                        }
                      }}
                      disabled={isSelectionDisabled}
                      className={`flex min-h-0 flex-1 flex-col text-left ${
                        isImageLoading
                          ? 'cursor-wait'
                          : 'disabled:cursor-not-allowed'
                      }`}
                    >
                      <DMText
                        as="span"
                        className="relative block h-[15.4vh] overflow-hidden rounded-[7px]"
                        style={{ background: avatar.background }}
                      >
                        {isFailed ? (
                          <FailedAvatarPlaceholder />
                        ) : (
                          <>
                            {isImageLoading && (
                              <DMText
                                as="span"
                                className="absolute inset-0 z-[1] animate-pulse bg-gray-200"
                              />
                            )}
                            <DMImage
                              src={
                                avatar.image || fallbackAvatarOptions[0].image
                              }
                              alt={avatar.label}
                              className={`h-full w-full object-contain object-bottom transition-opacity duration-200 ${
                                isImageLoading ? 'opacity-0' : 'opacity-100'
                              }`}
                              onLoad={() =>
                                setAvatarImageStates(states => ({
                                  ...states,
                                  [avatar.id]: 'loaded',
                                }))
                              }
                              onError={event => {
                                if (
                                  !event.currentTarget.dataset.fallbackApplied
                                ) {
                                  event.currentTarget.dataset.fallbackApplied =
                                    'true';
                                  event.currentTarget.src =
                                    fallbackAvatarOptions[0].image;
                                  return;
                                }

                                setAvatarImageStates(states => ({
                                  ...states,
                                  [avatar.id]: 'loaded',
                                }));
                              }}
                            />
                          </>
                        )}
                      </DMText>
                      <DMText
                        as="span"
                        className="mt-[1vh] text-center text-[1.3vh] font-bold text-gray-900"
                      >
                        {avatar.label}
                      </DMText>
                    </DMButton>
                    {isSelected && !isFailed && (
                      <DMText
                        as="span"
                        className="absolute right-[0.8vw] top-[1.2vh] flex h-[2.4vh] w-[2.4vh] items-center justify-center rounded-full bg-blue-600 text-white"
                      >
                        <Check
                          className="h-[1.6vh] w-[1.6vh]"
                          strokeWidth={3}
                        />
                      </DMText>
                    )}
                    <DMButton
                      type="button"
                      onClick={() => setAvatarToDelete(avatar)}
                      disabled={avatar.isDefault}
                      title={
                        avatar.isDefault
                          ? 'Default avatars cannot be deleted'
                          : `Delete ${avatar.label}`
                      }
                      className="absolute left-[0.8vw] top-[1.2vh] z-[2] flex h-[2.4vh] w-[2.4vh] items-center justify-center rounded-full bg-white/90 text-red-500 shadow-sm transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-white/90"
                      aria-label={
                        avatar.isDefault
                          ? `${avatar.label} cannot be deleted`
                          : `Delete ${avatar.label}`
                      }
                    >
                      <Trash2
                        className="h-[1.45vh] w-[1.45vh]"
                        strokeWidth={2.2}
                      />
                    </DMButton>
                    <DMText
                      as="span"
                      title={
                        avatar.isDefault ? 'Default Avatar' : 'Custom Avatar'
                      }
                      className={`absolute bottom-[1vh] right-[0.8vw] flex h-[2.4vh] w-[2.4vh] items-center justify-center rounded-full bg-white/95 shadow-sm ${
                        avatar.isDefault ? 'text-amber-500' : 'text-blue-600'
                      }`}
                      aria-label={
                        avatar.isDefault ? 'Default Avatar' : 'Custom Avatar'
                      }
                    >
                      {avatar.isDefault ? (
                        <Star
                          className="h-[1.45vh] w-[1.45vh] fill-current"
                          strokeWidth={2.1}
                        />
                      ) : (
                        <UserRound
                          className="h-[1.45vh] w-[1.45vh]"
                          strokeWidth={2.1}
                        />
                      )}
                    </DMText>
                  </DMDiv>
                );
              })}
          </DMDiv>

          <DMDiv className="mt-[1.6vh] shrink-0">
            <PaginationBar
              pageIndex={pageIndex}
              pageSize={pageSize}
              totalRecords={totalAvatars}
              onPageChange={setPageIndex}
              onPageSizeChange={setPageSize}
              itemName="avatars"
              pageSizeOptions={[16, 32, 48]}
            />
          </DMDiv>
        </DMDiv>

        <DMDiv
          as="section"
          className="flex min-h-0 flex-col rounded-[10px] border border-gray-200 bg-white px-[1.25vw] py-[2vh]"
        >
          <DMText
            as="h2"
            className="mb-[1.8vh] text-[1.9vh] font-bold text-gray-950"
          >
            Preview
          </DMText>
          <DMButton
            type="button"
            onClick={handleToggleAvatarPreview}
            onContextMenu={event => event.preventDefault()}
            disabled={isLoadingAvatars || !isSelectedAvatarPreviewable}
            className="relative h-[52.5vh] overflow-hidden rounded-[8px] bg-gray-950 text-left disabled:cursor-default"
            style={{ background: selectedAvatar.background }}
            aria-label={`Preview ${selectedAvatar.label}`}
          >
            {isLoadingAvatars ? (
              <DMText
                as="span"
                className="absolute inset-0 animate-pulse bg-gray-200"
              />
            ) : isSelectedAvatarFailed ? (
              <FailedAvatarPlaceholder />
            ) : selectedAvatar.videoUrl ? (
              <DMVideo
                key={selectedAvatar.videoUrl}
                ref={previewVideoRef}
                src={selectedAvatar.videoUrl}
                playsInline
                preload="metadata"
                controls={false}
                disablePictureInPicture
                controlsList="nodownload nofullscreen noremoteplayback"
                onContextMenu={event => event.preventDefault()}
                onLoadStart={() =>
                  setIsPreviewBuffering(previewShouldPlayRef.current)
                }
                onCanPlay={event => {
                  setIsPreviewBuffering(false);

                  if (
                    previewShouldPlayRef.current &&
                    event.currentTarget.paused
                  ) {
                    void startAvatarPreview(event.currentTarget);
                  }
                }}
                onPlaying={() => {
                  setIsPreviewPlaying(true);
                  setIsPreviewBuffering(false);
                }}
                onWaiting={() => {
                  if (previewShouldPlayRef.current) {
                    setIsPreviewBuffering(true);
                  }
                }}
                onPause={() => setIsPreviewPlaying(false)}
                onEnded={() => {
                  previewShouldPlayRef.current = false;
                  setIsPreviewPlaying(false);
                  setIsPreviewBuffering(false);
                }}
                className="mx-auto h-full w-full bg-gray-950 object-contain object-bottom"
              />
            ) : (
              <DMText
                as="span"
                className="flex h-full w-full items-center justify-center bg-gray-950 text-[1.5vh] font-semibold text-gray-400"
              >
                Video unavailable
              </DMText>
            )}
            {isSelectedAvatarProcessing && <ProcessingOverlay />}
            {isPreviewBuffering && isSelectedAvatarPreviewable && (
              <DMText
                as="span"
                className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/20"
              >
                <Loader2
                  className="h-[5.4vh] w-[5.4vh] animate-spin text-white drop-shadow-md"
                  strokeWidth={2.2}
                />
              </DMText>
            )}
            {!isPreviewBuffering && isSelectedAvatarPreviewable && (
              <DMText
                as="span"
                className="absolute left-1/2 top-1/2 flex h-[8.8vh] w-[8.8vh] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/75 bg-white/35 text-white shadow-[0_10px_28px_rgba(15,23,42,0.18)] backdrop-blur-sm"
              >
                {isPreviewPlaying ? (
                  <Pause
                    className="h-[4.4vh] w-[4.4vh] fill-white"
                    strokeWidth={1.8}
                  />
                ) : (
                  <Play
                    className="ml-[0.35vw] h-[5vh] w-[5vh] fill-white"
                    strokeWidth={1.6}
                  />
                )}
              </DMText>
            )}
          </DMButton>

          <DMText className="mt-[1.7vh] text-center text-[1.7vh] font-bold text-gray-950">
            {selectedAvatar.label}
          </DMText>
          <DMDiv className="mt-[1.7vh] border-t border-gray-200 pt-[1.8vh]">
            <DMButton
              type="button"
              onClick={handleSaveChanges}
              disabled={isLoadingAvatars || !paginatedAvatars.length}
              className="h-[5.2vh] w-full rounded-[7px] bg-blue-600 text-[1.6vh] font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Save Changes
            </DMButton>
          </DMDiv>
        </DMDiv>
      </DMDiv>
      <DigitalMarketingDeleteModal
        isOpen={!!avatarToDelete}
        title="Are you sure you want to delete this avatar?"
        description="This action cannot be undone, and this avatar will be removed."
        isDeleting={isDeletingAvatar}
        onCancel={() => setAvatarToDelete(null)}
        onConfirm={handleConfirmDeleteAvatar}
      />
    </DMDiv>
  );
}
